/**
 * Section B7.4 Service - B.7.4.減量移除計算
 * Calculation of emission reductions or net removals
 *
 * 架構說明：每筆 EquationEntry 含全部 4 頁籤資料，以 updateEquation 單一端點儲存。
 * 試算表附件使用 fileAttachmentsService 獨立管理，有附件時更新 has_spreadsheet=true。
 * equation_from 存 system_codes.id（code_etype='equation from type'），動態從 API 載入。
 */
import axios from '../api/axios';

const BASE_URL = '/api/section-b7-4';

// ── equations JSON 子結構 ─────────────────────────────────────────

export interface MethodChoiceItem {
  symbol: string;
  para_type?: number | null;   // 249=事前固定, 250=事後監測, null=皆適用
  scenarios: string;            // HTML 富文本
  unit: string;                 // LaTeX 語法
  annual_value?: number | null; // 保守評估階段填寫 (10,2)
}

export interface CalculationResult {
  value: number | null;
  unit: string;
}

export interface EquationEntry {
  id: string;                             // UUID v4
  // Tab 1: 計算公式
  equation_from: number | null;           // system_codes.id，code_etype='equation from type'
  name: string;                           // 來源名稱（方法學名稱 / 工具名稱等）
  equation: string;                       // LaTeX 公式
  equation_note: string;                  // 富文本，差異說明（選填）
  // Tab 2: 方法論說明
  method_choices: MethodChoiceItem[];
  // Tab 3: 計算年度預估值
  calculation_result: CalculationResult;
  conservative_description: string | null;
}

// ── 主資料表 ──────────────────────────────────────────────────────

export interface SectionB74Detail {
  id: number;
  organization_id: number;
  project_id: number;
  equations: EquationEntry[] | null;
  b7_4: number;
  section_c_complete: boolean;
  crediting_period_start: string | null;
  crediting_period_duration: number | null;
  edit_by: number;
  created_at: string | null;
  updated_at: string | null;
}

// ── Request Payloads ──────────────────────────────────────────────

export interface EquationEntryCreate {
  equation_from: number | null;
  name: string;
  equation: string;
  equation_note: string;
}

export interface EquationEntryUpdate {
  equation_from?: number | null;
  name?: string;
  equation?: string;
  equation_note?: string;
  method_choices?: MethodChoiceItem[];
  calculation_result?: CalculationResult;
  conservative_description?: string | null;
}

// ── API 函式 ──────────────────────────────────────────────────────

export const getSectionB74ByProject = async (projectId: number): Promise<SectionB74Detail | null> => {
  const response = await axios.get<SectionB74Detail>(`${BASE_URL}/by-project/${projectId}`);
  return response.data;
};

export const addEquation = async (projectId: number, entry: EquationEntryCreate): Promise<SectionB74Detail> => {
  const response = await axios.post<SectionB74Detail>(`${BASE_URL}/${projectId}/equations`, entry);
  return response.data;
};

export const updateEquation = async (
  projectId: number,
  entryId: string,
  entry: EquationEntryUpdate
): Promise<SectionB74Detail> => {
  const response = await axios.put<SectionB74Detail>(`${BASE_URL}/${projectId}/equations/${entryId}`, entry);
  return response.data;
};

export const deleteEquation = async (projectId: number, entryId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/${projectId}/equations/${entryId}`);
};

export const downloadSpreadsheetTemplate = async (projectId: number): Promise<void> => {
  const response = await axios.get(`${BASE_URL}/${projectId}/spreadsheet`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `B76_emission_reduction_estimate_${projectId}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const resetSectionB74 = async (projectId: number): Promise<void> => {
  await axios.post(`${BASE_URL}/${projectId}/reset`);
};

const sectionB74Service = {
  getSectionB74ByProject,
  addEquation,
  updateEquation,
  deleteEquation,
  downloadSpreadsheetTemplate,
  resetSectionB74,
};
export default sectionB74Service;
